/**
 * Per-deployment segments of an OPC vendor node path.
 *
 * Kone KICs expose identical leaf node names ("LiftMode", "DoorMode.Side1") at
 * every building; only the path above the leaf differs. The adapter therefore
 * holds only the leaves and stays valid for a controller type, while these
 * segments are entered per bank and per lift.
 *
 * Each name is the level of the OPC tree it populates, deliberately: all four
 * levels are independent, so a name that does not state its target level gets
 * wired to the wrong one.
 */
export const OPC_VENDOR_PATH_FIELDS = [
  "vendor_location_path",
  "vendor_site_path",
  "vendor_group_path",
] as const;

export type OpcVendorPathField = (typeof OPC_VENDOR_PATH_FIELDS)[number];

/**
 * Operator-facing labels. Each names the LEVEL as well as the field, because the
 * levels are positional: they are the first three dotted segments of the node
 * path above the lift, whatever the vendor calls its own tiers (Kone Moffitt
 * reads `MoffittSperos.Location.Group0.SE1`, where the literal word "Location"
 * is the SECOND tier). Every label still contains the plain name it had before
 * the level suffix was added, so substring matches on it keep working.
 */
export const OPC_VENDOR_PATH_LABELS: Record<OpcVendorPathField, string> = {
  vendor_location_path: "OPC Location Path (level 1)",
  vendor_site_path: "OPC Site Path (level 2)",
  vendor_group_path: "OPC Group Path (level 3)",
};

/** `general.*` translation key of the info tooltip explaining each segment. */
export const OPC_VENDOR_PATH_HELP_KEYS: Record<OpcVendorPathField, string> = {
  vendor_location_path: "general.bankOpcVendorLocationPathHelp",
  vendor_site_path: "general.bankOpcVendorSitePathHelp",
  vendor_group_path: "general.bankOpcVendorGroupPathHelp",
};

/**
 * camelCase spelling of each field.
 *
 * A bank reaches the standalone form over either the FlatBuffer path
 * (camelCase, from the Object API) or the JSON API (snake_case), so both
 * spellings have to be read and cleared. Written out rather than derived by a
 * string transform: an explicit map is greppable, type-checked against the
 * field union, and cannot silently produce a key nothing reads.
 */
export const OPC_VENDOR_PATH_CAMEL: Record<OpcVendorPathField, string> = {
  vendor_location_path: "vendorLocationPath",
  vendor_site_path: "vendorSitePath",
  vendor_group_path: "vendorGroupPath",
};

/** Per-lift segments; the lift editor renders these next to Car Number. */
export const OPC_VENDOR_LIFT_FIELDS = ["vendor_lift_path", "vendor_group_name"] as const;

export type OpcVendorLiftField = (typeof OPC_VENDOR_LIFT_FIELDS)[number];

export const OPC_VENDOR_LIFT_LABELS: Record<OpcVendorLiftField, string> = {
  vendor_lift_path: "OPC Lift Path",
  vendor_group_name: "OPC Group Name",
};

/** camelCase spelling of each per-lift field; see `OPC_VENDOR_PATH_CAMEL`. */
export const OPC_VENDOR_LIFT_CAMEL: Record<OpcVendorLiftField, string> = {
  vendor_lift_path: "vendorLiftPath",
  vendor_group_name: "vendorGroupName",
};

/**
 * Protocols whose banks carry vendor path segments.
 *
 * Both OPC dialects address points by a dotted node path; no other protocol in
 * the system does, so the fields stay hidden everywhere else.
 */
export const OPC_PATH_PROTOCOLS = new Set(["OPC", "OPCUA"]);

/**
 * The three bank-level path segments as a form holds them. Each value may be
 * absent, null or blank: an empty input, a bank that never had the segment, and
 * a segment cleared by the operator all mean "not set".
 */
export type OpcVendorPathValues = Partial<Record<OpcVendorPathField, string | null | undefined>>;

const isNonBlank = (value: string | null | undefined): value is string =>
  typeof value === "string" && value.trim() !== "";

/**
 * How filled in the three bank-level segments are.
 *
 * LiftCloud composes a node path only when ALL THREE are non-empty and otherwise
 * leaves the node id as the adapter has it, so the two in-between states are the
 * dangerous ones: a bank with only some of them set looks configured and does
 * nothing. Whitespace-only counts as blank, as it does when the value is saved.
 *
 * - `"empty"`: all three blank.
 * - `"complete"`: all three set.
 * - `"partial"`: anything else.
 */
export function opcVendorPathState(values: OpcVendorPathValues): "empty" | "partial" | "complete" {
  const filled = OPC_VENDOR_PATH_FIELDS.filter((field) => isNonBlank(values[field])).length;
  if (filled === 0) return "empty";
  return filled === OPC_VENDOR_PATH_FIELDS.length ? "complete" : "partial";
}

/**
 * The node path LiftCloud will compose for a lift in this bank:
 * `Location.Site.Group[.LiftPath].<leaf>`, every segment trimmed.
 *
 * Mirrors LiftCloud's `opcUaBankPrefix` + `joinNodeSegments`: all three bank
 * segments or none (so anything but a `"complete"` state returns `null`, there
 * being nothing to show), and an empty lift segment is skipped rather than
 * leaving an empty dotted component.
 *
 * @param values the bank's three segments
 * @param liftPath the lift's own `vendor_lift_path`, when one is known
 * @param leaf stand-in for the adapter's leaf node name
 */
export function composeOpcUaNodePreview(
  values: OpcVendorPathValues,
  liftPath: string | null | undefined,
  leaf = "<leaf>",
): string | null {
  if (opcVendorPathState(values) !== "complete") return null;
  const segments = OPC_VENDOR_PATH_FIELDS.map((field) => (values[field] ?? "").trim());
  if (isNonBlank(liftPath)) segments.push(liftPath.trim());
  segments.push(leaf);
  return segments.join(".");
}

/**
 * What the partial-state rule does with the segments as they stand now.
 *
 * - `"ok"`: nothing to say (`"empty"` or `"complete"`).
 * - `"block"`: the segments are partial and the operator just put them there, or
 *   the bank is new. Saving is refused: a path that is only part filled is not
 *   composed, and nothing would tell the operator.
 * - `"warn"`: the segments are partial but the bank ALREADY was when it was
 *   loaded. It stays editable so unrelated fields can still be saved, and the
 *   operator is told what the two ways out are.
 *
 * @param initial the segments as the bank was loaded, `null` for a bank being
 *   created
 * @param current the segments as the form holds them
 */
export function opcVendorPathTransition(
  initial: OpcVendorPathValues | null,
  current: OpcVendorPathValues,
): "ok" | "warn" | "block" {
  if (opcVendorPathState(current) !== "partial") return "ok";
  if (initial === null || opcVendorPathState(initial) !== "partial") return "block";
  return "warn";
}

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null;

/**
 * The three bank-level segments out of a bank record that arrived under either
 * spelling (camelCase from the FlatBuffer Object API, snake_case from the JSON
 * API), camelCase winning when it is a string — the same precedence the editors
 * use when they read the value into an input and when they save it. Anything
 * that is not a record, or a segment that is not a string, reads as unset.
 */
export function readOpcVendorPathValues(bank: unknown): OpcVendorPathValues {
  const values: OpcVendorPathValues = {};
  if (!isRecord(bank)) return values;
  OPC_VENDOR_PATH_FIELDS.forEach((field) => {
    const camel = bank[OPC_VENDOR_PATH_CAMEL[field]];
    const raw = typeof camel === "string" ? camel : bank[field];
    values[field] = typeof raw === "string" ? raw : null;
  });
  return values;
}

/**
 * The `vendor_lift_path` of the first lift in a bank's loaded lift list (lowest
 * `position`, load order breaking ties), or `null` when no lift is loaded or the
 * first one has none. Used only to make the composed-path preview concrete; it
 * never reaches a payload.
 */
export function firstLiftVendorPath(lifts: unknown): string | null {
  if (!Array.isArray(lifts)) return null;
  const ranked = lifts
    .map((lift, order) => ({
      lift,
      order,
      position: isRecord(lift) && typeof lift.position === "number" ? lift.position : 0,
    }))
    .sort((a, b) => a.position - b.position || a.order - b.order);
  const first = ranked[0]?.lift;
  if (!isRecord(first)) return null;
  const camel = first[OPC_VENDOR_LIFT_CAMEL.vendor_lift_path];
  const raw = typeof camel === "string" ? camel : first.vendor_lift_path;
  return typeof raw === "string" ? raw : null;
}
